import { AfterViewInit, Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { DatePickerPresets, GlobalFunctions, SnackType } from '@global/GlobalFunctions';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { AccountFeatures, AccountsService, IAccount } from '@services/accounts.service';
import { AssetsDataService, LevelAsset } from '@services/assets-data.service';
import { AuthenticationService } from '@services/authentication.service';
import { Rule, RulesDataService } from '@services/rules-data.service';
import { combineLatest, distinctUntilChanged, filter, finalize, firstValueFrom, Subject, take, takeUntil } from 'rxjs';
import { convertEpochToDateString } from '@functions/functions'
import { EventsDataService, RuleEvent, OfflineEvent, WebhookEvent, DeviationEvent } from '@services/events-data.service';
import { MatSort } from '@angular/material/sort';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { TabItem } from '@syncfusion/ej2-angular-navigations';
import { OrganisationsHierarchy } from '@services/organisation-data.service';
import { DropDownList } from '@syncfusion/ej2-angular-dropdowns';
import { ModelsService, ModelType } from '@services/models.service';

/** Used to filter the table of rules */
export interface FilterData {
	Acknowledged: boolean;
	Unacknowledged: boolean;
	Unarchived: boolean;
	Archived: boolean;
	Location?: OrganisationsHierarchy;
}

/**
 * Default Class
 */
@Component({
	selector: 'app-events',
	templateUrl: './events.component.html',
	styleUrls: ['./events.component.scss']
})
export class EventsComponent implements OnInit, OnDestroy, AfterViewInit {

	/** Observable Subject Finalise */
	private finalise = new Subject<void>();

	/** ruleEvents */
	public ruleEvents: RuleEvent[];

	/** offlineEvents */
	public offlineEvents: OfflineEvent[];

	/** Webhook Events */
	public webhookEvents: WebhookEvent[];

	/** Filtered Rule Events */
	public filteredRuleEvents: RuleEvent[];

	/** Filtered Offline Events */
	public filteredOfflineEvents: OfflineEvent[];

	/** Filtered Webhook Events */
	public filteredWebhookEvents: WebhookEvent[];

	/** Deviation Events */
	public deviationEvents: DeviationEvent[];

	/** Filtered Deviation Events */
	public filteredDeviationEvents: DeviationEvent[];

	/** Rules array */
	public rules: any[] = [];

	/** Asset array */
	public assets: any[] = [];

	public fields: Object = { text: 'RuleText', id: 'RuleEventID' };
	public fieldsOffline: Object = { text: 'OfflineEventText', id: 'OfflineEventID' };
	public fieldsWebhook: Object = { text: 'WebhookEventText', id: 'WebhookEventID' };

	public SelectedEvent?: RuleEvent;

	public EventRule: Rule;

	public EventAsset: LevelAsset;

	/** strDuration */
	public strDuration: string = ''; //'00:12:00';
	/** Define start date */
	public startDate: Date | undefined;
	/** Define end date */
	public endDate: Date | undefined;
	/** paramStartDate */
	public paramStartDate: Date;
	/** paramEndDate */
	public paramEndDate: Date;

	/** maxMonths */
	private maxMonths: number = 36; // max months increased as retrieving data for long periods no longer an issue

	formattedStartDate: string | undefined;
	formattedEndDate: string | undefined;

	public selectedTab: number = 0;

	public isFiltering: boolean = false;

	/** accountID */
	@Input() accountID: string;
	/** selectedAccount */
	public selectedAccount: IAccount;
	/** OrganisationID */
	@Input() public OrganisationUID: string;

	public CMMSFeatureEnabled: boolean = false;

	/** displayedColumns */
	displayedColumnsRule: string[] = [];
	/** displayedColumns */
	displayedColumns: string[] = ['timestamp', 'rule', 'comments', 'acknowledged', 'CaseID', 'archiveButton'];

	/** displayedColumns */
	displayedColumnsOffline: string[] = ['timestamp', 'sensor', 'acknowledged'];

	/** displayedColumns */
	displayedColumnsWebhook: string[] = ['timestamp', 'webhook', 'type'];

	/** displayedColumns */
	displayedColumnsDeviation: string[] = ['timestamp', 'model', 'asset', 'zscore', 'acceptableDeviation', 'acknowledged'];

	@ViewChild(MatPaginator) paginator: MatPaginator;

	/** datasource */
	dataSourceRule: MatTableDataSource<RuleEvent>;

	/** Table paginator */
	@ViewChild('paginatorRule') paginatorRule: MatPaginator;
	/** Table sorting */
	@ViewChild(MatSort) sortRule: MatSort;

	/** datasource */
	dataSourceOffline: MatTableDataSource<OfflineEvent>;

	/** Table paginator */
	@ViewChild('paginatorOffline') paginatorOffline: MatPaginator;
	/** Table sorting */
	@ViewChild(MatSort) sortOffline: MatSort;

	/** datasource */
	dataSourceWebhook: MatTableDataSource<WebhookEvent>;

	/** Table paginator */
	@ViewChild('paginatorWebhook') paginatorWebhook: MatPaginator;
	/** Table sorting */
	@ViewChild(MatSort) sortWebhook: MatSort;

	/** datasource */
	dataSourceDeviation: MatTableDataSource<DeviationEvent>;

	/** Table paginator */
	@ViewChild('paginatorDeviation') paginatorDeviation: MatPaginator;
	/** Table sorting */
	@ViewChild(MatSort) sortDeviation: MatSort;

	@ViewChild('rulesTab') rulesTab: TabItem;

	private queryFrom: number = 0;
	private queryTo: number = 0;

	/** Initialised filterData */
	public filterData: FilterData = {
		Acknowledged: false, Unacknowledged: false, Unarchived: true, Archived: false, Location: undefined
	}

	public hierarchy: OrganisationsHierarchy[];

	public flattenedAssetIDs: number[] = [];

	/** shortMonths */
	private shortMonths: string[] = [
		"Jan", "Feb", "Mar", "Apr", "May", "Jun",
		"Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
	];

	/** MainLocations */
	public MainLocations: OrganisationsHierarchy[] = [];

	/** locationFields */
	public locationFields: { text: string, value: string } = { text: "Name", value: "Id" };

	/** selectedLocation */
	public selectedLocation?: OrganisationsHierarchy;

	/** locationDropdown */
	@ViewChild('dropdown') locationDropdown: DropDownList;

	/** modelTypes */
	public modelTypes: ModelType[] = [];

	/** today - #71 */
	public today: Date = new Date();

	/** datePickerPresets - #71 */
	public datePickerPresets = DatePickerPresets;

	/**
	 * Default Constructor
	 * @param rulesDataService rulesDataService
	 * @param assetsDataService assetsDataService
	 * @param eventsDataService eventsDataService
	 * @param modelsService modelsService
	 * @param dialog dialog
	 * @param authenticationService authenticationService
	 * @param siteSubscriptions siteSubscriptions
	 * @param router router
	 * @param route Activated Route
	 * @param globalFunctions globalFunctions
	 */
	constructor(
		public rulesDataService: RulesDataService,
		public assetsDataService: AssetsDataService,
		public eventsDataService: EventsDataService,
		public modelsService: ModelsService,
		public dialog: MatDialog,
		public authenticationService: AuthenticationService,
		public siteSubscriptions: siteSubscriptions,
		private router: Router,
		private route: ActivatedRoute,
		private globalFunctions: GlobalFunctions
	) {
		this.globalFunctions.displaySpinner(true);

		combineLatest([
			this.siteSubscriptions.hierarchicalData$.pipe(distinctUntilChanged()),
			this.route.queryParamMap.pipe(distinctUntilChanged())
		]).pipe(
			takeUntil(this.finalise),
			// Ensure that subscriptions are not null or undefined
			filter(([hierarchicalData, queryParamMap]) =>
				!!hierarchicalData && !!queryParamMap)
		).subscribe({
			next: async ([hierarchicalData, queryParamMap]) => {

				let [selectedAccount, selectedAccountGUID, selectedOrganisation, selectedOrganisationUID] = await firstValueFrom(
					combineLatest([
						this.siteSubscriptions.selectedAccount$.pipe(take(1)),
						this.siteSubscriptions.selectedAccountGUID$.pipe(take(1)),
						this.siteSubscriptions.selectedOrganisation$.pipe(take(1)),
						this.siteSubscriptions.selectedOrganisationUID$.pipe(take(1))
					]
					)
				);

				this.accountID = selectedAccountGUID;
				this.selectedAccount = selectedAccount
				this.OrganisationUID = selectedOrganisationUID;

				this.CMMSFeatureEnabled = AccountsService.checkFeature(AccountFeatures.SAP, this.selectedAccount);
				if (!this.CMMSFeatureEnabled) { this.displayedColumnsRule = this.displayedColumns.filter(column => column !== 'CaseID'); }
				else { this.displayedColumnsRule = this.displayedColumns; }

				let urlAccountID = this.router.url.split('/')[1];

				if (this.accountID.toLowerCase() != urlAccountID.toLowerCase() && this.router.url.includes('event') && !this.router.url.includes('routing#')) {
					this.router.navigateByUrl(`routing#${this.router.url}`);
				}

				this.hierarchy = JSON.parse(hierarchicalData);

				this.MainLocations = [];

				if (this.hierarchy[0].SubChild) {
					this.hierarchy[0].SubChild.forEach(subchild => {
						this.MainLocations.push(subchild);
					});
				}

				let list: LevelAsset[] = this.flattenAssets(this.hierarchy);
				list.forEach(asset => {
					this.flattenedAssetIDs.push(asset.AssetID);
				})

				var savedTab = queryParamMap.get('selectedTab') ?? ""
				switch (savedTab) {
					case "Rule":
						this.selectedTab = 0;
						break;
					case "Sensor":
						this.selectedTab = 1;
						break;
					case "Deviation":
						this.selectedTab = 2;
						break;
					case "Webhook":
						this.selectedTab = 3;
						break;
				}
				// this.ruleEvents = [];

				if (queryParamMap.get('from')) {
					if (this.queryTo != Number(queryParamMap.get('to')) || this.queryFrom != Number(queryParamMap.get('from'))) {
						this.paramStartDate = new Date(Number(queryParamMap.get('from')));
						this.paramEndDate = new Date(Number(queryParamMap.get('to')));
						this.startDate = this.paramStartDate;
						this.endDate = this.paramEndDate;
						this.getRuleEvents(this.startDate.getTime(), this.endDate.getTime());
						this.getOfflineEvents(this.startDate.getTime(), this.endDate.getTime());
						this.getWebhookEvents(this.startDate.getTime(), this.endDate.getTime());
						this.getDeviationEvents(this.startDate.getTime(), this.endDate.getTime());
					}
					this.queryTo = Number(queryParamMap.get('to'));
					this.queryFrom = Number(queryParamMap.get('from'));
					this.formattedStartDate = this.makeDateSmall(new Date(this.queryFrom))
					this.formattedEndDate = this.makeDateSmall(new Date(this.queryTo))
				} else {
					this.getRuleEvents();
					this.getOfflineEvents();
					this.getWebhookEvents();
					this.getDeviationEvents();
				}
			},
			error: (error) => {
				this.globalFunctions.HandleError(error, "Error: Error in one of the combined subscriptions", false);
			}
		});
	}

	/**
	 * getOfflineEvents
	 * @param timeStampFrom timeStampFrom 
	 * @param timeStampTo timeStampTo 
	 */
	getOfflineEvents(timeStampFrom: number = 0, timeStampTo: number = 0) {
		const fetchDataSubject = new Subject<void>();
		this.offlineEvents = [];
		this.filteredOfflineEvents = [];
		this.eventsDataService.getOfflineEvents$(this.accountID, this.OrganisationUID, 0, timeStampTo, timeStampFrom).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((offlineEvents) => !!offlineEvents),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
				this.dataSourceOffline = new MatTableDataSource(this.filteredOfflineEvents);
				this.dataSourceOffline.paginator = this.paginatorOffline;
				this.dataSourceOffline.sort = this.sortOffline;
				this.globalFunctions.displaySpinner(false)
				this.assignPaginators();
			})
		).subscribe({
			next: (offlineEvents: any) => {
				this.offlineEvents = offlineEvents.filter((x: OfflineEvent) => this.checkContainedAssetIDs(x.AssetIDs) == true);
				this.filteredOfflineEvents = this.offlineEvents;
				this.filterRules();
			},
			error: (error) => {
				if (error.status == 404) {
					this.filteredOfflineEvents = []
				}
			}
		});
	}

	/**
	 * getWebhookEvents
	 * @param timeStampFrom timeStampFrom 
	 * @param timeStampTo timeStampTo 
	 */
	getWebhookEvents(timeStampFrom: number = 0, timeStampTo: number = 0) {
		const fetchDataSubject = new Subject<void>();
		this.webhookEvents = [];
		this.filteredWebhookEvents = [];
		this.eventsDataService.getWebhookEvents$(this.accountID, this.OrganisationUID, 0, timeStampTo, timeStampFrom).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((webhookEvents) => !!webhookEvents),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
				this.dataSourceWebhook = new MatTableDataSource(this.filteredWebhookEvents);
				this.dataSourceWebhook.paginator = this.paginatorWebhook;
				this.dataSourceWebhook.sort = this.sortWebhook;
				this.globalFunctions.displaySpinner(false)
				this.assignPaginators();
			})
		).subscribe({
			next: (webhookEvents: any) => {
				this.webhookEvents = webhookEvents;
				this.filteredWebhookEvents = this.webhookEvents;
				this.filterRules();
			},
			error: (error) => {
				if (error.status == 404) {
					this.filteredWebhookEvents = []
				}
			}
		});
	}

	/**
	 * getDeviationEvents
	 * @param timeStampFrom timeStampFrom 
	 * @param timeStampTo timeStampTo 
	 */
	getDeviationEvents(timeStampFrom: number = 0, timeStampTo: number = 0) {
		const fetchDataSubject = new Subject<void>();
		this.deviationEvents = [];
		this.filteredDeviationEvents = [];
		this.eventsDataService.getDeviationEvents$(this.accountID, this.OrganisationUID, 0, 0, timeStampFrom, timeStampTo).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((deviationEvents) => !!deviationEvents),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
				this.dataSourceDeviation = new MatTableDataSource(this.filteredDeviationEvents);
				this.dataSourceDeviation.paginator = this.paginatorOffline;
				this.dataSourceDeviation.sort = this.sortOffline;
				this.globalFunctions.displaySpinner(false)
				this.assignPaginators();
			})
		).subscribe({
			next: (deviationEvents: any) => {
				this.deviationEvents = deviationEvents.filter((x: DeviationEvent) => this.flattenedAssetIDs.includes(x.AssetID));
				this.filteredDeviationEvents = this.deviationEvents;
				this.filterRules();
				this.getModelTypes();
			},
			error: (error) => {
				if (error.status == 404) {
					this.filteredDeviationEvents = []
				}
			}
		});
	}

	/**
	 * tabChange
	 * @param event event  
	 */
	tabChange(event: any) {
		this.selectedTab = event.selectedIndex;
		let tabName: string = "";
		switch (event.selectedIndex) {
			case 0:
				setTimeout(() => {
					this.dataSourceRule.paginator = this.paginatorRule;
					this.dataSourceRule.sort = this.sortRule;
					this.dataSourceRule.sortingDataAccessor = (item, property) => {
						switch (property) {
							case 'timestamp': return item.TimeStamp || '';
							case 'rule': return item.RuleName || '';
							default: return '';
						}
					};
				}, 10);
				tabName = "Rule";
				break;
			case 1:
				setTimeout(() => {
					this.dataSourceOffline.paginator = this.paginatorOffline;
				}, 10);
				tabName = "Sensor";
				break;
			case 2:
				setTimeout(() => {
					this.dataSourceDeviation.paginator = this.paginatorDeviation;
				}, 10);
				tabName = "Deviation";
				break;
			case 3:
				setTimeout(() => {
					this.dataSourceWebhook.paginator = this.paginatorWebhook;
				}, 10);
				tabName = "Webhook";
				break;
		}
		this.router.navigate([], {
			relativeTo: this.route,
			queryParams: { selectedTab: tabName },
			queryParamsHandling: 'merge'
		});
	}

	/** returns true if any of the provided assetIDs are contained
	 * 	withing the flattened assets available to this user
	 */
	checkContainedAssetIDs(assetIDs?: string): boolean {
		let result: boolean = false;
		if (assetIDs) {
			let arrIDs = assetIDs.split(",");
			arrIDs.forEach(assetID => {
				if (this.flattenedAssetIDs.includes(parseInt(assetID))) {
					result = true;
				}
			})
		}
		return result;
	}

	/** Getting list of assets from an input hierarchy */
	flattenAssets(hierarchy: OrganisationsHierarchy[]): any[] {
		let result: any[] = [];

		// Check if hierarchy is a valid array before proceeding
		if (!Array.isArray(hierarchy)) {
			return result;
		}

		// Iterate over each item in the input hierarchy
		for (const item of hierarchy) {
			// Check if the item has a non-empty 'Asset' property
			if (item.Assets && item.Assets.length > 0) {
				result = result.concat(item.Assets);
			}

			// Recursively add all 'Asset' properties of the item's subchildren
			result = result.concat(this.flattenAssets(item.SubChild));
		}

		return result;
	}

	/**
	 * Page On Initialise Event Handler
	 */
	ngOnInit(): void {
	}

	/**
	 * Page after view init Event Handler
	 */
	ngAfterViewInit(): void {
		// switch (this.selectedTab) {
		// 	case 0:
		// 		setTimeout(() => {
		// 			this.dataSourceRule.paginator = this.paginatorRule;
		// 		}, 1500);
		// 		break;
		// 	case 1:
		// 		setTimeout(() => {
		// 			this.dataSourceOffline.paginator = this.paginatorOffline;
		// 		}, 1500);
		// 		break;
		// 	case 2:
		// 		setTimeout(() => {
		// 			this.dataSourceWebhook.paginator = this.paginatorWebhook;
		// 		}, 1500);
		// 		break;
		// }
		this.assignPaginators();
	}

	/**
	 * assignPaginators
	 * @returns 
	 */
	assignPaginators() {
		if (this.selectedTab == 0 && this.paginatorRule) {
			this.dataSourceRule.paginator = this.paginatorRule;
			return;
		}
		else if (this.selectedTab == 1 && this.paginatorOffline) {
			this.dataSourceOffline.paginator = this.paginatorOffline;
			return;
		}
		else if (this.selectedTab == 3 && this.paginatorWebhook) {
			this.dataSourceWebhook.paginator = this.paginatorWebhook;
			return;
		} else if (this.selectedTab == 2 && this.paginatorDeviation) {
			this.dataSourceDeviation.paginator = this.paginatorDeviation;
			return;
		} else {
			setTimeout(() => {
				this.assignPaginators();
			}, 100);
		}
	}

	/**
	 * Page On Destroy Event Handler
	 */
	ngOnDestroy(): void {
		try {
			// Unsubscribe from all subscriptions
			this.finalise.next();
			this.finalise.complete();
		}
		catch { }
	}

	/**
	 * getAndPushRules
	 */
	getAndPushRules() {

		for (let i = 0; i < this.ruleEvents.length; i++) {
			// Create a local Subject to control the subscription lifecycle
			const fetchDataSubject = new Subject<void>();
			this.rulesDataService.getRules$(this.accountID, this.OrganisationUID, 0, this.ruleEvents[i].RuleID).pipe(
				takeUntil(fetchDataSubject), distinctUntilChanged(),
				finalize(() => {
					// Ensure the subscription is terminated and the subject is completed
					fetchDataSubject.next();
					fetchDataSubject.complete();
				})
			).subscribe({
				next: (rules: any) => {
					if (rules) {
						this.rules.push(rules[0]);
					}
					if (this.rules.length == this.ruleEvents.length) {
						// this.getAndPushAssets()
						this.globalFunctions.displaySpinner(false)

					}
				},
				// error: (error) => { this.globalFunctions.HandleError(error, "myAccounts Error: Retrieving Accounts", false) },
				// complete: () => { console.log("myAccounts: Retrieving Accounts: Complete");
			});
		}
	}

	/** Filters rule table based on filterData */
	filterRules() {
		if (!this.ruleEvents || !this.offlineEvents) {
			return
		}
		if (this.filterData.Archived) {
			this.filteredRuleEvents = this.ruleEvents.filter(ruleEvent => ruleEvent.Archived === true)
		} else {
			if (this.filterData.Acknowledged) {
				this.filteredRuleEvents = this.ruleEvents.filter((ruleEvent: RuleEvent) => ruleEvent.Acknowledged && !ruleEvent.Archived);
				this.filteredOfflineEvents = this.offlineEvents.filter((offlineEvent: OfflineEvent) => offlineEvent.Acknowledged);
				this.filteredDeviationEvents = this.deviationEvents.filter((deviationEvent: DeviationEvent) => deviationEvent.Acknowledged);
			} else if (this.filterData.Unacknowledged) {
				this.filteredRuleEvents = this.ruleEvents.filter((ruleEvent: RuleEvent) => !ruleEvent.Acknowledged && !ruleEvent.Archived);
				this.filteredOfflineEvents = this.offlineEvents.filter((offlineEvent: OfflineEvent) => !offlineEvent.Acknowledged);
				this.filteredDeviationEvents = this.deviationEvents.filter((deviationEvent: DeviationEvent) => !deviationEvent.Acknowledged);
			} else {
				this.filteredRuleEvents = this.ruleEvents.filter((x: RuleEvent) => !x.Archived);
				this.filteredOfflineEvents = this.offlineEvents;
				this.filteredDeviationEvents = this.deviationEvents;
			}
		}
		if (this.filterData.Location) {
			let locationAssetIDs: number[] = this.flattenAssets([this.filterData.Location]).map((x: LevelAsset) => x.AssetID);
			this.filteredRuleEvents = this.filteredRuleEvents.filter((x: RuleEvent) => locationAssetIDs.includes(x.AssetID));
			this.filteredOfflineEvents = this.filteredOfflineEvents.filter((x: OfflineEvent) => x.AssetIDs?.split(",")
				.some((y: string) => locationAssetIDs.includes(parseInt(y))));
		}
		this.dataSourceRule = new MatTableDataSource(this.filteredRuleEvents);
		this.dataSourceRule.paginator = this.paginatorRule;
		this.dataSourceRule.sort = this.sortRule;
		this.dataSourceOffline = new MatTableDataSource(this.filteredOfflineEvents);
		this.dataSourceOffline.paginator = this.paginatorOffline;
		this.dataSourceOffline.sort = this.sortOffline;
		this.dataSourceDeviation = new MatTableDataSource(this.filteredDeviationEvents);
		this.dataSourceDeviation.paginator = this.paginatorDeviation;
		this.dataSourceDeviation.sort = this.sortDeviation;
	}

	/** filterActiveRules */
	filterAcknowledgedRules(event: any) {
		this.filterData.Acknowledged = event.checked
		this.filterRules();
	}

	/** filterActiveRules */
	filterUnacknowledgedRules(event: any) {
		this.filterData.Unacknowledged = event.checked
		this.filterRules();
	}

	/** filterArchivedEvents */
	filterArchivedEvents(event: any) {
		this.filterData.Archived = event.checked;
		this.filterRules();
	}

	/**
	 * deselectEvent
	 */
	deselectEvent() {
		this.SelectedEvent = undefined
	}

	/**
	 * selectEvent
	 * @param data
	 */
	selectEvent(data: RuleEvent) {
		this.router.navigate([`./${this.accountID}/organisation/${this.OrganisationUID}/event/${data.RuleEventID}`], {
			queryParams: { eventType: '2' }
		});
	}

	selectOfflineEvent(data: OfflineEvent) {
		this.router.navigate([`./${this.accountID}/organisation/${this.OrganisationUID}/event/${data.OfflineEventID}`], {
			queryParams: { eventType: '1' }
		});
	}

	selectWebhookEvent(data: WebhookEvent) {
		this.router.navigate([`./${this.accountID}/organisation/${this.OrganisationUID}/event/${data.WebhookEventID}`], {
			queryParams: { eventType: '3' }
		});
	}

	selectDeviationEvent(data: DeviationEvent) {
		this.router.navigate([`./${this.accountID}/organisation/${this.OrganisationUID}/event/${data.DeviationEventID}`], {
			queryParams: { eventType: '4' }
		});
	}

	/** getRule */
	getRule(ruleID: number): Rule | undefined {
		return this.rules.find((x: Rule) => x.RuleID == ruleID);
	}

	/**
	 * getAsset
	 * @param ruleID
	 * @returns
	 */
	getAsset(ruleID: number): LevelAsset | undefined {
		let rule = this.rules.find((x: Rule) => x.RuleID == ruleID);
		return this.assets.find((x: LevelAsset) => x.AssetID == rule?.AssetID)
	}

	/**
	 * convertEpoch
	 * @param epoch
	 * @returns
	 */
	convertEpoch(epoch: number): string {
		/** Use the Global Function */
		return convertEpochToDateString(epoch);
	}

	/**
	 * splitValues
	 * @param text
	 * @returns
	 */
	splitValues(text: string): string[] { return text.split(';') }

	/**
	 * Converts int of total minutes to [HH,MM] string array
	 * @param totalMinutes
	 * @returns
	 */
	convertMinutesToHHMM(totalMinutes: number): string[] {
		const hours = Math.floor(totalMinutes / 60);
		const minutes = totalMinutes % 60;
		const hoursString = hours.toString().padStart(2, '0');
		const minutesString = minutes.toString().padStart(2, '0');
		return [hoursString, minutesString]
	}

	webhookEventTypeToText(type: number): string {
		switch (type) {
			case 0:
				return "Webhook Offline"
			case 1:
				return "Webhook Offline"
			default:
				return ""
		}
	}

	/**
	 * onFiltering
	 * @param event
	 */
	onFilteringRuleEvents(event: any): void {
		event.cancel = true;
		const query = event.target.value.toLowerCase();
		this.filteredRuleEvents = this.ruleEvents.filter(data => {
			const dateStr = `${convertEpochToDateString(data.TimeStamp)}`.toLowerCase();
			const ruleDescription = `${data.RuleName}`.toLowerCase() || '';
			const assetInfo = `${data.AssetName || ''}`.toLowerCase();
			const ruleEventID = data.RuleEventID.toString().toLowerCase();
			const acknowledgedBy = data.AcknowledgedUser;
			const acknowledgedOn = this.getDate(data.AcknowledgedTimestamp).toLowerCase();
			const comments = data.Comments.toLowerCase();

			// Combine the necessary fields for filtering
			const combinedStr = `${dateStr} ${ruleDescription} ${assetInfo} ${ruleEventID} ${acknowledgedBy} ${acknowledgedOn} ${comments}`;
			return combinedStr.includes(query);
		});
		this.dataSourceRule = new MatTableDataSource(this.filteredRuleEvents);
		this.dataSourceRule.paginator = this.paginatorRule;
		this.dataSourceRule.sort = this.sortRule;
	}

	/**
	 * onFiltering
	 * @param event
	 */
	onFilteringOfflineEvents(event: any): void {
		event.cancel = true;
		const query = event.target.value.toLowerCase();
		this.filteredOfflineEvents = this.offlineEvents.filter(data => {
			const dateStr = `${convertEpochToDateString(data.TimeStamp)}`.toLowerCase();
			const ruleDescription = `${data.SensorName}`.toLowerCase() || '';

			// Combine the necessary fields for filtering
			const combinedStr = `${dateStr} ${ruleDescription}`;
			return combinedStr.includes(query);
		});
		this.dataSourceOffline = new MatTableDataSource(this.filteredOfflineEvents);
		this.dataSourceOffline.paginator = this.paginatorOffline;
		this.dataSourceOffline.sort = this.sortOffline;
	}

	/**
	 * onFiltering
	 * @param event
	 */
	onFilteringWebhookEvents(event: any): void {
		event.cancel = true;
		const query = event.target.value.toLowerCase();
		this.filteredWebhookEvents = this.webhookEvents.filter(data => {
			const dateStr = `${convertEpochToDateString(data.TimeStamp)}`.toLowerCase();
			const ruleDescription = `${data.ClientWebhookID}`.toLowerCase() || '';

			// Combine the necessary fields for filtering
			const combinedStr = `${dateStr} ${ruleDescription}`;
			return combinedStr.includes(query);
		});
		this.dataSourceWebhook = new MatTableDataSource(this.filteredWebhookEvents);
		this.dataSourceWebhook.paginator = this.paginatorWebhook;
		this.dataSourceWebhook.sort = this.sortWebhook;
	}

	onFilteringDeviationEvents(event: any): void {
		event.cancel = true;
		const query = event.target.value.toLowerCase();
		this.filteredDeviationEvents = this.deviationEvents.filter(data => {
			const dateStr = `${convertEpochToDateString(data.Timestamp)}`.toLowerCase();
			const asset = `${data.AssetName} - ${data.AssetDescription}`.toLowerCase() || '';

			// Combine the necessary fields for filtering
			const combinedStr = `${dateStr} ${asset}`;
			return combinedStr.includes(query);
		});
		this.dataSourceDeviation = new MatTableDataSource(this.filteredDeviationEvents);
		this.dataSourceDeviation.paginator = this.paginatorDeviation;
		this.dataSourceDeviation.sort = this.sortDeviation;
	}

	/**
	 * DatePickerChange
	 * @param event
	 */
	DatePickerChange(event: any) {
		try {
			this.startDate = undefined;
			this.endDate = undefined;
			if (event.daySpan == 0) {
				this.resetQueryDates();
				return
			}
			this.isFiltering = true;
			// Validate the Date Range < 6 Months
			// Validate the Date Range < 6 Months
			const date1 = new Date(event.startDate.getTime());
			const date2 = new Date(event.endDate.getTime());

			const diffInMs = Math.abs(date2.getTime() - date1.getTime());
			const diffInMonths = diffInMs / (1000 * 60 * 60 * 24 * 30.44);

			if (diffInMonths < this.maxMonths) {
				// console.log('The difference is less than ' + this.maxMonths + ' months');

				this.startDate = event.startDate;
				this.endDate = event.endDate

				if (this.startDate && this.endDate) {
					this.formattedStartDate = this.makeDateSmall(this.startDate);
					this.formattedEndDate = this.makeDateSmall(this.endDate);

					/** Add a day to retrieve data for last day too */
					this.endDate.setDate(this.endDate.getDate() + 1)

					/** Reset Duration Search */
					this.strDuration = "custom";
					this.rebuildURL(this.startDate.getTime(), this.endDate.getTime())
				}
			}
		}
		catch (error) { this.globalFunctions.HandleError(error, "DatePickerChange Error: Unknown", false) }
	}

	/**
	 * makeDateSmall
	 * @param date date 
	 * @returns string
	 */
	makeDateSmall(date: Date): string {
		const daysOfWeek = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
		const dayOfWeek = daysOfWeek[date.getDay()];
		const day = String(date.getDate()).padStart(2, '0');
		const month = String(date.getMonth() + 1).padStart(2, '0');
		const year = date.getFullYear();
		return `${dayOfWeek} ${day}/${month}/${year}`;

	}

	/**
	 * resetQueryDates
	 */
	resetQueryDates() {
		this.formattedStartDate = undefined;
		this.formattedEndDate = undefined;
		this.isFiltering = false;
		this.queryFrom = 0;
		this.queryTo = 0;
		this.rebuildURL(0, 0);
	}

	/**
	 * getRuleEvents
	 * @param timeFrom timeFrom  
	 * @param timeTo timeTo 
	 */
	getRuleEvents(timeFrom: number = 0, timeTo: number = 0) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();
		this.eventsDataService.getRuleEvents$(this.accountID, this.OrganisationUID, 0, 0, timeFrom, timeTo).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((ruleEvents) => !!ruleEvents),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
				this.dataSourceRule = new MatTableDataSource(this.filteredRuleEvents);
				this.dataSourceRule.paginator = this.paginatorRule;
				this.dataSourceRule.sort = this.sortRule;
				this.globalFunctions.displaySpinner(false)
				this.assignPaginators();
			})
		).subscribe({
			next: (ruleEvents: any) => {
				this.ruleEvents = ruleEvents.filter((x: RuleEvent) => this.flattenedAssetIDs.includes(x.AssetID));
				this.filteredRuleEvents = this.ruleEvents
				this.filterRules();
				// this.getAndPushRules()
			},
			error: (error) => {
				if (error.status == 404) {
					this.filteredRuleEvents = []
				}
			}
		});
	}

	/**
	 * Rebuild an URL for refreshing page
	 * @param start Start Date
	 * @param end End Date
	 * @param sensors Array of sensors
	 */
	rebuildURL(start: any = this.paramStartDate.getTime(), end: any = this.paramEndDate.getTime()): void {
		let params = {
			'from': start == 0 ? undefined : start,
			'to': end == 0 ? undefined : end,
		}
		const url = this.router.createUrlTree([], { relativeTo: this.route }).toString()
		this.router.navigate([url], {
			queryParams: params,
			queryParamsHandling: 'merge'
		}
		)
	}

	/** applyFilter */
	applyFilterRules(event: Event) {
		const filterValue = (event.target as HTMLInputElement).value;
		this.dataSourceRule.filter = filterValue.trim().toLowerCase();

		if (this.dataSourceRule.paginator) {
			this.dataSourceRule.paginator.firstPage();
		}
	}

	/**
	 * archiveRuleEvent
	 * @param ruleEvent ruleEvent  
	 */
	archiveRuleEvent(ruleEvent: RuleEvent) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		if (ruleEvent.Acknowledged) {
			this.eventsDataService.archiveRuleEvent$(this.accountID, this.OrganisationUID, ruleEvent.RuleEventID).pipe(
				takeUntil(fetchDataSubject), distinctUntilChanged(),
				filter((ruleEvents) => !!ruleEvents),
				finalize(() => {
					// Ensure the subscription is terminated and the subject is completed
					fetchDataSubject.next();
					fetchDataSubject.complete();
				})
			).subscribe({
				next: (ruleEvents: any) => {
					this.getRuleEvents();
					this.globalFunctions.DisplaySnackBar({
						message: "Event Archived",
						duration: 2000,
						type: SnackType.Info,
						action: ""
					})
				},
				error: (error) => {
					if (error.status == 404) {
						this.filteredRuleEvents = []
					}
				}
			});
		} else {
			this.globalFunctions.DisplaySnackBar({
				message: "Cannot Archive an Unacknowledged Event",
				duration: 2000,
				type: SnackType.Error,
				action: ""
			})
		}
	}

	selectCase(ruleEvent: RuleEvent) {
		this.router.navigate([
			this.route.snapshot.paramMap.get('account'),
			'organisation',
			this.route.snapshot.paramMap.get('organisation'),
			'SAPCases',
			ruleEvent.CaseID
		]);
	}

	/**
	 * getDate
	 * @param epoch epoch  
	 * @returns 
	 */
	getDate(epoch: number): string {
		let date: Date = new Date(Number(epoch));
		return this.getShortDate(date);
	}

	/** getShortDate */
	getShortDate(date: Date): string {
		let result: string = "";
		result += date.getDate();
		result +=
			" " +
			this.shortMonths[date.getMonth()] +
			" " +
			date.getHours().toString().padStart(2, "0") +
			":" +
			date.getMinutes().toString().padStart(2, "0");

		return result;
	}

	/**
	 * filterByLocation
	 * @param event event 
	 * @param cancel cancel 
	 */
	filterByLocation(event: any, cancel?: boolean) {
		if (cancel) {
			this.selectedLocation = undefined;
			this.filterData.Location = undefined;
			this.locationDropdown.clear();
		} else if (event.itemData) {
			this.selectedLocation = <OrganisationsHierarchy>event.itemData;
			this.filterData.Location = this.selectedLocation;
		}
		this.filterRules();
	}

	/**
	 * getModelTypes
	 */
	getModelTypes() {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.modelsService.getModelTypes$(this.accountID, this.OrganisationUID).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((modelTypes) => !!modelTypes),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
				this.globalFunctions.displaySpinner(false)
			})
		).subscribe({
			next: (modelTypes: any) => {
				this.modelTypes = modelTypes;
			},
			error: (error) => {
				if (error.status === 404) {

				}
			}
		});
	}

	/**
	 * getModelTypeName
	 * @param modelTypeID modelTypeID  
	 * @returns 
	 */
	getModelTypeName(modelTypeID: number): string {
		let result: string = "";
		let modelType = this.modelTypes.find((x: ModelType) => x.ModelTypeID == modelTypeID);
		if (modelType) {
			result = modelType.ModelName;
		}
		return result;
	}
}
